import clickEvent from '@/app/components/search/Curiosity/clickEvent.ts';
import { ClickEventNameEnum } from '@/app/components/search/Curiosity/Analytics/utils/ClickEventNameEnum.ts';
import type { CommonData } from '@/app/common/createCommonData.ts';
import { type CuriosityParams, RouteType } from './index';
import type { Localization } from '../common/Localization';

export type CuriosityClickEvents = {
  alternativeFlexibleDates: (value: string) => void;
  alternativeFlexibleDatesCloseModal: () => void;
  alternativeFlexibleDatesOpenModal: () => void;
  comparisonAdd: () => void;
  comparisonModalClick: (value: string) => void;
  comparisonModalClose: () => void;
  comparisonModalOpen: () => void;
  comparisonModalRemove: () => void;
  comparisonRemove: () => void;
  comparisonToastCompare: () => void;
  comparisonToastRemove: () => void;
  comparisonTravelExtraPackCloseModal: () => void;
  comparisonTravelExtraPackOpenModal: () => void;
  customCheckinCheckoutBannerShow: () => void;
  customCheckinCheckoutModalApply: (value: string) => void;
  customCheckinCheckoutModalClear: () => void;
  customCheckinCheckoutModalClose: () => void;
  customCheckinCheckoutModalOpen: () => void;
  customCheckinCheckoutShow: () => void;
  deferredPaymentsFilterDetails: () => void;
  filtersDone: (value: string) => void;
  filtersReset: (value: string) => void;
  flightExpiredMessageClose: () => void;
  flightExpiredMessageShown: () => void;
  hotelDetailsClick: ({
    name,
    context,
    subContext,
    value,
    additionalValue,
  }: {
    name: string;
    context?: string;
    value?: string;
    subContext?: string;
    additionalValue?: string;
  }) => void;
  indexResultsDiscoverMore: () => void;
  indexResultsShowMore: (value: string) => void;
  mapAirportMarker: (subContext: string | undefined) => void;
  mapHotelDotClick: () => void;
  mapMarker: () => void;
  mapPanning: () => void;
  mapSelectAirport: (subContext: string | undefined) => void;
  mapShowAirports: () => void;
  mapSkiLiftMarker: () => void;
  mapStyleTypeDefault: () => void;
  mapStyleTypeSatellite: () => void;
  mapZoomIn: () => void;
  mapZoomOut: () => void;
  mobileFiltersOpen: (routeType: RouteType) => void;
  opxFlexibleBannerClick: (value: string) => void;
  overnightFlights: (context: string) => void;
  packageSavingsModalClose: () => void;
  packageSavingsModalOpen: () => void;
  searchPaginationNext: (routeType: RouteType, value: string) => void;
  searchPaginationPage: (routeType: RouteType, value: string) => void;
  searchPaginationPrev: (routeType: RouteType, value: string) => void;
  sustainabilityBannerShow: () => void;
  travelExtraPackCloseModal: () => void;
  travelExtraPackOpenModal: () => void;
  viewModeToggle: (value: string) => void;
  voucherExtraBannerClick: (value: string) => void;
  voucherExtraBannerShow: () => void;
};

type FireClickEvent = {
  name: string;
  routeType: RouteType;
  context?: string;
  subContext?: string;
  value?: string;
  additionalValue?: string;
};

type CreateCuriosityClickEvents = {
  commonData: CommonData;
  localization: Localization;
  curiosityParams: CuriosityParams;
};
export const createCuriosityClickEvents = ({
  commonData,
  localization,
  curiosityParams,
}: CreateCuriosityClickEvents): CuriosityClickEvents => {
  const fireClickEvent = ({ name, routeType, context, subContext, value }: FireClickEvent) => {
    clickEvent({
      clickParams: {
        event_name: name,
        event_context: context,
        event_subcontext: subContext,
        event_value: value,
      },
      routeType,
      commonData,
      localization,
      curiosityParams,
    });
  };

  return {
    alternativeFlexibleDates: (value: string) => {
      fireClickEvent({ name: 'alternative_flexible_dates', routeType: RouteType.OSMAP, subContext: 'link', value });
    },
    alternativeFlexibleDatesCloseModal: () => {
      fireClickEvent({ name: 'alternative_flexible_dates_close_modal', routeType: RouteType.OSMAP });
    },
    alternativeFlexibleDatesOpenModal: () => {
      fireClickEvent({ name: 'alternative_flexible_dates_open_modal', routeType: RouteType.OSMAP });
    },
    comparisonAdd: () => {
      fireClickEvent({ name: 'comparison_add', routeType: RouteType.OSMAP });
    },
    comparisonModalClick: (value: string) => {
      fireClickEvent({ name: 'comparison_modal_click', routeType: RouteType.OSMAP, subContext: 'link', value });
    },
    comparisonModalClose: () => {
      fireClickEvent({ name: 'comparison_modal_close', routeType: RouteType.OSMAP });
    },
    comparisonModalOpen: () => {
      fireClickEvent({ name: 'comparison_modal_open', routeType: RouteType.OSMAP });
    },
    comparisonModalRemove: () => {
      fireClickEvent({ name: 'comparison_modal_remove', routeType: RouteType.OSMAP });
    },
    comparisonRemove: () => {
      fireClickEvent({ name: 'comparison_remove', routeType: RouteType.OSMAP });
    },
    comparisonToastCompare: () => {
      fireClickEvent({ name: 'comparison_toast_compare', routeType: RouteType.OSMAP });
    },
    comparisonToastRemove: () => {
      fireClickEvent({ name: 'comparison_toast_remove', routeType: RouteType.OSMAP });
    },
    comparisonTravelExtraPackCloseModal: () => {
      fireClickEvent({ name: 'comparison_travel_extra_pack_close_modal', routeType: RouteType.OSMAP });
    },
    comparisonTravelExtraPackOpenModal: () => {
      fireClickEvent({ name: 'comparison_travel_extra_pack_open_modal', routeType: RouteType.OSMAP });
    },
    customCheckinCheckoutBannerShow: () => {
      fireClickEvent({ name: 'custom_checkin_checkout_banner_show', routeType: RouteType.OSMAP });
    },
    customCheckinCheckoutModalApply: (value: string) => {
      fireClickEvent({
        name: 'custom_checkin_checkout_modal_apply',
        routeType: RouteType.OSMAP,
        subContext: 'button',
        value,
      });
    },
    customCheckinCheckoutModalClear: () => {
      fireClickEvent({ name: 'custom_checkin_checkout_modal_clear', routeType: RouteType.OSMAP, subContext: 'button' });
    },
    customCheckinCheckoutModalClose: () => {
      fireClickEvent({ name: 'custom_checkin_checkout_modal_close', routeType: RouteType.OSMAP, subContext: 'button' });
    },
    customCheckinCheckoutModalOpen: () => {
      fireClickEvent({ name: 'custom_checkin_checkout_modal_open', routeType: RouteType.OSMAP, subContext: 'button' });
    },
    customCheckinCheckoutShow: () => {
      fireClickEvent({ name: 'custom_checkin_checkout_show', routeType: RouteType.OSMAP });
    },
    deferredPaymentsFilterDetails: () => {
      fireClickEvent({ name: 'deferred_payments_filter_details', routeType: RouteType.OSMAP, subContext: 'button' });
    },
    filtersDone: (value: string) => {
      fireClickEvent({
        name: ClickEventNameEnum.FiltersDone,
        routeType: RouteType.OSMAP,
        subContext: 'button',
        value,
      });
    },
    filtersReset: (value: string) => {
      fireClickEvent({
        name: ClickEventNameEnum.FiltersReset,
        routeType: RouteType.OSMAP,
        subContext: 'button',
        value,
      });
    },
    flightExpiredMessageClose: () => {
      fireClickEvent({ name: 'flight_expired_message_close', routeType: RouteType.OSMAP, subContext: 'button' });
    },
    flightExpiredMessageShown: () => {
      fireClickEvent({ name: 'flight_expired_message_shown', routeType: RouteType.OSMAP, subContext: 'button' });
    },
    hotelDetailsClick: ({ name, subContext, value, additionalValue }) => {
      fireClickEvent({ name, routeType: RouteType.HOTELDETAILS, subContext, value, additionalValue });
    },
    indexResultsDiscoverMore: () => {
      fireClickEvent({
        name: ClickEventNameEnum.DiscoverMore,
        routeType: RouteType.OSMAP,
        subContext: 'button',
      });
    },
    indexResultsShowMore: (value: string) => {
      fireClickEvent({ name: ClickEventNameEnum.ShowMore, routeType: RouteType.OSMAP, subContext: 'button', value });
    },
    mapAirportMarker: (subContext: string | undefined) => {
      fireClickEvent({ name: 'map_airport_marker', routeType: RouteType.OSMAP, subContext });
    },
    mapHotelDotClick: () => {
      fireClickEvent({ name: ClickEventNameEnum.hotelDotClick, routeType: RouteType.OSMAP, subContext: 'map-dot' });
    },
    mapMarker: () => {
      fireClickEvent({ name: ClickEventNameEnum.MapMarker, routeType: RouteType.OSMAP });
    },
    mapPanning: () => {
      fireClickEvent({ name: ClickEventNameEnum.MapPanning, routeType: RouteType.OSMAP });
    },
    mapSelectAirport: (subContext: string | undefined) => {
      fireClickEvent({ name: 'select_airport_click', routeType: RouteType.OSMAP, subContext });
    },
    mapShowAirports: () => {
      fireClickEvent({ name: 'map_show_airports', routeType: RouteType.OSMAP });
    },
    mapSkiLiftMarker: () => {
      fireClickEvent({ name: 'map_skilift_marker', routeType: RouteType.OSMAP });
    },
    mapStyleTypeDefault: () => {
      fireClickEvent({ name: 'map_style_type_default', routeType: RouteType.OSMAP });
    },
    mapStyleTypeSatellite: () => {
      fireClickEvent({ name: 'map_style_type_satellite', routeType: RouteType.OSMAP });
    },
    mapZoomIn: () => {
      fireClickEvent({ name: ClickEventNameEnum.MapZoomIn, routeType: RouteType.OSMAP });
    },
    mapZoomOut: () => {
      fireClickEvent({ name: ClickEventNameEnum.MapZoomOut, routeType: RouteType.OSMAP });
    },
    mobileFiltersOpen: (routeType: RouteType) => {
      fireClickEvent({ name: ClickEventNameEnum.MobileFiltersOpen, routeType, subContext: 'button' });
    },
    opxFlexibleBannerClick: (value: string) => {
      fireClickEvent({ name: 'opx_flexible_banner_click', routeType: RouteType.OSMAP, value });
    },
    overnightFlights: (context: string) => {
      fireClickEvent({ name: 'overnight_flights', routeType: RouteType.OSMAP, context });
    },
    packageSavingsModalClose: () => {
      fireClickEvent({ name: 'package_savings_modal_close', routeType: RouteType.OSMAP });
    },
    packageSavingsModalOpen: () => {
      fireClickEvent({ name: 'package_savings_modal_open', routeType: RouteType.OSMAP });
    },
    searchPaginationNext: (routeType: RouteType, value: string) => {
      fireClickEvent({
        name: ClickEventNameEnum.SearchPaginationNext,
        routeType,
        subContext: 'button',
        value,
      });
    },
    searchPaginationPage: (routeType: RouteType, value: string) => {
      fireClickEvent({
        name: ClickEventNameEnum.SearchPaginationPage,
        routeType,
        subContext: 'button',
        value,
      });
    },
    searchPaginationPrev: (routeType: RouteType, value: string) => {
      fireClickEvent({
        name: ClickEventNameEnum.SearchPaginationPrev,
        routeType,
        subContext: 'button',
        value,
      });
    },
    sustainabilityBannerShow: () => {
      fireClickEvent({ name: 'sustainability_banner_show', routeType: RouteType.SEARCHRESULTS });
    },
    travelExtraPackCloseModal: () => {
      fireClickEvent({ name: 'travel_extra_pack_close_modal', routeType: RouteType.OSMAP });
    },
    travelExtraPackOpenModal: () => {
      fireClickEvent({ name: 'travel_extra_pack_open_modal', routeType: RouteType.OSMAP });
    },
    viewModeToggle: (value: string) => {
      fireClickEvent({ name: 'viewmode_toggle', routeType: RouteType.OSMAP, subContext: 'button', value });
    },
    voucherExtraBannerClick: (value: string) => {
      fireClickEvent({ name: 'voucher_extra_banner_click', routeType: RouteType.OSMAP, value });
    },
    voucherExtraBannerShow: () => {
      fireClickEvent({ name: 'voucher_extra_banner_show', routeType: RouteType.OSMAP });
    },
  };
};
